import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { PLAN, type Plan } from '@ovrsee/shared'
import { ApiError } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { Button, Chip, Input, Text } from '../../ui/index.js'

const PLAN_LABELS: Record<Plan, string> = {
  [PLAN.STARTER]: 'Starter',
  [PLAN.GROWTH]: 'Growth',
  [PLAN.ENTERPRISE]: 'Enterprise',
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso))
}

export function OrganisationTab() {
  const { api, user, reload } = useAuth()
  const organisation = user?.organisation ?? null
  const [name, setName] = useState(organisation?.name ?? '')
  const [saved, setSaved] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (organisation === null) return
    setName(organisation.name)
    setFormError(null)
  }, [organisation?.id, organisation?.name])

  const updateOrganisation = useMutation({
    mutationFn: (nextName: string) => api.patch('/api/organisation', { name: nextName }),
    onSuccess: async () => {
      setSaved(true)
      await reload()
    },
  })

  if (organisation === null) return null

  const trimmed = name.trim()
  const canSave = trimmed !== '' && trimmed !== organisation.name && !updateOrganisation.isPending

  return (
    <div style={{ display: 'grid', gap: 'var(--o-space-5)' }}>
      <section
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--o-space-5)',
        }}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (!canSave) return
            setFormError(null)
            void updateOrganisation.mutateAsync(trimmed).catch((error) => {
              setFormError(error instanceof ApiError ? error.message : 'Could not rename the organisation.')
            })
          }}
        >
          <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
            <label className="field-label" htmlFor="organisation-name">
              Organisation name
            </label>
            <Input
              id="organisation-name"
              aria-label="Organisation name"
              value={name}
              onChange={(value) => {
                setName(value)
                setSaved(false)
              }}
            />
          </div>

          <div className="row-actions" style={{ justifyContent: 'flex-start', marginBottom: 'var(--o-space-3)' }}>
            <Button kind="primary" type="submit" disabled={!canSave}>
              Save
            </Button>
          </div>

          {saved ? <Text>Saved.</Text> : null}
          {formError !== null ? (
            <Text color="secondary" style={{ display: 'block', marginTop: 'var(--o-space-2)' }}>
              {formError}
            </Text>
          ) : null}
        </form>
      </section>

      <section
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--o-space-5)',
          display: 'grid',
          gap: 'var(--o-space-3)',
        }}
      >
        <div className="row-between" style={{ alignItems: 'center' }}>
          <Text style={{ fontWeight: 600 }}>Plan</Text>
          <Chip label={PLAN_LABELS[organisation.plan as Plan]} />
        </div>

        {organisation.trialEndsAt !== null ? (
          <div className="row-between" style={{ alignItems: 'center' }}>
            <Text style={{ fontWeight: 600 }}>Trial ends</Text>
            <Text>{formatDate(organisation.trialEndsAt)}</Text>
          </div>
        ) : null}

        <Text color="secondary">Billing management arrives in a later release.</Text>
      </section>
    </div>
  )
}
